import {TransactionId} from '@raison/types';
import {RsnDialog, RsnMessage} from '@raison/ui-kit';
import React, {useRef} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {
    createTransactionIdPath,
    FiatTransactionType,
    TransactionSchema,
    TransactionStatus,
} from '~/entities/transactions-history';
import {Decoder} from '@raison/prelude';
import {useTransactionDetails} from '../api/useTransactionDetails';
import {LowValueTransactionDialog} from './LowValueTransactionDialog';
import TransactionInfo from './TransactionInfo/TransactionInfo';
import TransactionMain from './TransactionMain/TransactionMain';
import TransactionMessage from './TransactionMessage/TransactionMessage';

const LOW_VALUE_DIALOG_KEY = 'lowValueDialog';

export const TransactionDetailsModal = ({
    transaction: transaction,
    transactionId,
}: {
    transaction: TransactionSchema | undefined;
    transactionId: TransactionId | undefined;
}) => {
    const navigate = useNavigate();
    const {data: details} = useTransactionDetails(transactionId, transaction);

    const isFiat = (type: string) => Decoder.check(FiatTransactionType)(type);
    const shouldDisplayMessage = (type: string, status: TransactionStatus) => {
        return isFiat(type) ? status !== 'pending' : true;
    };

    const {search, state} = useLocation();
    const openLowValueDialog = () => {
        navigate(createTransactionIdPath(undefined, search), {state: {...state, [LOW_VALUE_DIALOG_KEY]: true}});
    };

    const latestTransactionId = useRef<TransactionId>();
    if (transactionId) latestTransactionId.current = transactionId;

    return (
        <RsnDialog.Controller onClose={() => navigate(-1)}>
            {(details && (
                <RsnDialog.Window>
                    <RsnDialog.LeftHeader text={details.type_human} />
                    <RsnDialog.Body>
                        {details?.is_low_value && (
                            <div className={'pb-4'}>
                                <RsnMessage.Notification arrowed onClick={openLowValueDialog}>
                                    Warning: Unusual low-value transaction
                                </RsnMessage.Notification>
                            </div>
                        )}
                        {shouldDisplayMessage(details.type, details.status) && (
                            <TransactionMessage
                                status={details.status}
                                group={details.group}
                                message={details.rejected_message || ''}
                            />
                        )}
                        <TransactionMain details={details} />
                        <TransactionInfo details={details} />
                    </RsnDialog.Body>
                </RsnDialog.Window>
            )) ||
                (state?.[LOW_VALUE_DIALOG_KEY] && <LowValueTransactionDialog />)}
        </RsnDialog.Controller>
    );
};
